import {
    ChangeDetectionStrategy,
    Component,
    HostBinding,
    Input,
    OnDestroy,
    OnInit,
    ViewEncapsulation,
} from '@angular/core';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { FiContentLanguageSharedService } from '@fi-shared/fi-content-language-shared/fi-content-language-shared.service';
import { FiChecklistSectionTypes } from '@fi-shared/checklist';
import { FiChecklistHelperService } from '@fi-shared/checklist/fi-checklist-helper.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FiChecklistStoreService } from '@fi-shared/checklist/fi-checklist-store.service';
import { LoadingService } from '@fi-shared/fi-loading/loading.service';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { SaveChangesWarningService } from '@fi-shared/fi-shared-services/save-changes-warning.service';
import { IChecklistV2 } from '@fi-shared/checklist/checklist-v2';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { ObjectTypes, SideListOptionType } from '@fi-shared/types';
import { FiChecklist2Service } from '@fi-shared/checklist/fi-checklist2.service';
import { IAccessRightFilterCollection, IAccessRightSelectionFiltersCollection } from '@fi-shared/models';
import { FiChecklist2TemplateService } from '@fi-shared/checklist/fi-checklist2-template/fi-checklist2-template.service';
import { cloneDeep } from '@fi-shared/utils';
import { FiChecklistEditorStoreService } from '@fi-shared/checklist/fi-checklist-editor-store.service';

@Component({
    selector: 'fi-checklist2-edit',
    templateUrl: './fi-checklist2-edit.component.html',
    styleUrls: ['./fi-checklist2-edit.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-checklist2-edit' },
    standalone: false,
})
export class FiChecklist2EditComponent implements OnInit, OnDestroy {
    @HostBinding('class.actions-offset-bottom') @Input() isChatAvailable: boolean;
    @Input() templateMode: boolean;
    @Input({ required: true }) auditMode: boolean;
    @Input() isTemplateBased: boolean;
    @Input() accessRightFilterCollection: IAccessRightFilterCollection;
    @Input() templateId: number;
    @Input() savedQuickSelectionFilters: IAccessRightSelectionFiltersCollection[] = [];
    currentSection = FiChecklistSectionTypes.General;
    checklist: IChecklistV2;

    constructor(
        private userDataService: FiUserDataService,
        private fiContentLanguageSharedService: FiContentLanguageSharedService,
        private fiChecklistHelperService: FiChecklistHelperService,
        private fiChecklistStoreService: FiChecklistStoreService,
        private fiChecklistEditorStoreService: FiChecklistEditorStoreService,
        private loadingService: LoadingService,
        private fiStateService: FiStateService,
        private saveChangesWarningService: SaveChangesWarningService,
        private fiChecklist2Service: FiChecklist2Service,
        private fiChecklist2TemplateService: FiChecklist2TemplateService,
    ) {}

    ngOnInit(): void {
        this.checklist = this.fiChecklistEditorStoreService.getInitialChecklistData();
        this.startWarningChanges();
        this.getCurrentChecklistData();
        this.setTranslationData();
    }

    checklistChange(newChecklistData: IChecklistV2) {
        this.fiChecklistEditorStoreService.setCurrentChecklistData$(newChecklistData);
    }

    navigateToCheckpointSections() {
        this.checklistChange(this.checklist);
        this.saveChangesWarningService.stop();
        this.fiChecklistHelperService.navigateToChecklistCheckpointSectionsEditor(
            this.templateId,
            this.checklist.Id,
            this.accessRightFilterCollection,
        );
    }

    private getCurrentChecklistData() {
        this.fiChecklistEditorStoreService
            .getCurrentChecklistData$()
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe((checklist: IChecklistV2) => (this.checklist = cloneDeep(checklist)));
    }

    private setTranslationData() {
        this.fiChecklistStoreService.selectedLanguageId = this.userDataService.userData.PreferredContentLanguage;
        this.fiChecklistStoreService.availableContentLanguages =
            this.fiContentLanguageSharedService.getContentLanguages();
    }

    private startWarningChanges() {
        const currentObjectType: ObjectTypes = this.templateMode
            ? ObjectTypes.Checklist2Template
            : ObjectTypes.Checklist2;
        this.saveChangesWarningService.start(
            () => this.checklist,
            this.fiChecklistHelperService.getChecklistDiscardChangesDialogData(this.checklist.Id, currentObjectType),
        );
    }

    save() {
        const savePromise: Promise<number> = !this.templateMode
            ? this.fiChecklist2Service.saveChecklist(this.checklist, this.auditMode)
            : this.fiChecklist2TemplateService.saveChecklistTemplate(this.checklist, this.auditMode);

        this.loadingService.trackPromise(savePromise).then((data: number) => {
            this.checklist.Id = data;
            this.saveChangesWarningService.stop();

            // return to state by registered data, otherwise return to default state
            this.fiStateService.checkReturnToState(() => {
                // no saved state for returning, just go to list
                const rootState = FiStateNames.getChecklistV2RootState(this.auditMode);
                const filterType =
                    !this.auditMode && !this.templateMode
                        ? SideListOptionType.CurrentCheckLists
                        : SideListOptionType.SharedWithMe;
                if (!this.templateMode) {
                    this.fiStateService.go(rootState.List, { filterType: filterType });
                } else {
                    this.fiStateService.go(rootState.Template.List, { filterType: filterType });
                }
            });
        });
    }

    ngOnDestroy$: Subject<void> = new Subject<void>();
    ngOnDestroy() {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
